import { type ReactNode } from "react";

/** Arrangement of the label and title within the header. */
export type EntityCardLayout = "inline" | "stacked";

export interface EntityCardHeaderProps {
  /** Icon shown to the left of the label (rendered in a 14px slot) */
  icon: ReactNode;
  /** Short category label, e.g. "Account" or "Policy" */
  label: string;
  /** Primary title content (e.g. account or policy name) */
  title: ReactNode;
  /** Optional supplementary content rendered next to the title — chips, region badges, etc. */
  meta?: ReactNode;
  /**
   * Optional secondary content aligned to the top-right of the card. Only
   * rendered in the `"stacked"` layout (e.g. a "Deployed: …" timestamp).
   */
  aside?: ReactNode;
  /**
   * Arrangement of the label and title.
   * - `"inline"` (default): icon + label sit to the left, title shares the same row.
   * - `"stacked"`: icon + label sit on the top row, title on the row beneath it.
   */
  layout?: EntityCardLayout;
  /**
   * Trailing element aligned to the far right of the title row (e.g. a caret for
   * the link variant or an expand toggle for the expandable variant).
   */
  trailing?: ReactNode;
}

/**
 * Presentational header shared by the EntityCard variants. Pairs an icon + label
 * group with a title + meta group, arranged either `"inline"` (a single row) or
 * `"stacked"` (the label row above the title row). An optional `trailing` slot is
 * placed at the far right of the title row.
 *
 * Returns a fragment; the caller owns the flex container (a row for `"inline"`,
 * a column for `"stacked"`).
 */
export function EntityCardHeader({
  icon,
  label,
  title,
  meta,
  aside,
  layout = "inline",
  trailing,
}: EntityCardHeaderProps) {
  const iconSlot = (
    <span className="size-3.5 inline-flex shrink-0 items-center justify-center">{icon}</span>
  );
  const labelSpan = (
    <span className="text-label-medium font-medium capitalize text-text-secondary">{label}</span>
  );
  const titleSpan = (
    <span className="min-w-5 truncate text-label-medium text-text-primary">{title}</span>
  );
  const metaSlot = meta ? <span className="flex shrink-0 items-center">{meta}</span> : null;

  if (layout === "stacked") {
    return (
      <>
        <div className="flex w-full items-start justify-between gap-xs">
          <div className="flex min-w-0 items-center gap-xs">
            {iconSlot}
            {labelSpan}
          </div>
          {aside ? (
            <span className="shrink-0 text-label-small text-text-secondary">{aside}</span>
          ) : null}
        </div>
        <div className="flex w-full items-center justify-between gap-xs">
          <div className="flex min-w-0 items-center gap-xs overflow-hidden">
            {titleSpan}
            {metaSlot}
          </div>
          {trailing}
        </div>
      </>
    );
  }

  return (
    <>
      <div className="flex shrink-0 items-center gap-xs">
        {iconSlot}
        {labelSpan}
      </div>
      <div className="flex min-w-0 flex-1 items-center gap-xs overflow-hidden">
        {titleSpan}
        {metaSlot}
      </div>
      {trailing}
    </>
  );
}
